import React, { useCallback, memo } from 'react';
import { Box, Tabs, Tab, CircularProgress } from '@mui/material';
import { getSentence } from '../translations';
import { TABS_CONFIG } from './constants';
import { isSummarizedKey, summaryKey, keypointsKey, transcriptionKey } from '../MeetingsKeysList';
import { parseTranscription } from '../transcription/utils';

const MeetingTabs = memo(({ 
  tabValue, 
  onTabChange, 
  meeting 
}) => {
  const handleTabChange = useCallback((event, newValue) => {
    onTabChange?.(newValue, meeting);
  }, [onTabChange, meeting]);

  const isProcessing = meeting?.[isSummarizedKey] === 0;
  
  // Check content availability for each tab
  const hasContent = {
    summary: meeting?.[summaryKey] && meeting[summaryKey].trim() !== '',
    keypoints: meeting?.[keypointsKey] && meeting[keypointsKey].trim() !== '',
    transcription: (() => {
      if (!meeting?.[transcriptionKey] || meeting[transcriptionKey].trim() === '') {
        return false;
      }
      const parsed = parseTranscription(meeting[transcriptionKey]);
      return parsed && Array.isArray(parsed) && parsed.length > 0;
    })()
  };

  // Determine which tabs should show loading indicators
  const getTabLoadingState = (tabConfig) => {
    if (!isProcessing) return false; // No loading if not processing
    
    switch (tabConfig.type) {
      case 'summary':
        return !hasContent.summary;
      case 'keypoints':
        return !hasContent.keypoints;
      case 'transcription':
        return !hasContent.transcription;
      default:
        return false;
    }
  };

  // Add logging to track when MeetingTabs re-renders
  console.log('[MeetingTabs] Rendering with meeting:', {
    meetingId: meeting?.[0], // idKey is 0
    isSummarized: meeting?.[isSummarizedKey],
    timestamp: Date.now(),
    isProcessing,
    contentStatus: hasContent,
    loadingTabs: TABS_CONFIG.filter(tab => getTabLoadingState(tab)).map(tab => tab.type)
  });

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'relative' }}>
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        aria-label="meeting content tabs"
      >
        {TABS_CONFIG.map((tab) => {
          const isTabLoading = getTabLoadingState(tab);
          return (
            <Tab 
              key={tab.index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getSentence(tab.labelKey)}
                  {isTabLoading && (
                    <CircularProgress 
                      size={12} 
                      thickness={4}
                      sx={{ 
                        color: 'primary.main',
                        opacity: 0.7
                      }} 
                    />
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>
      
      {/* Processing indicator bar - only show if any tabs are still loading */}
      {isProcessing && TABS_CONFIG.some(tab => getTabLoadingState(tab)) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: 'linear-gradient(90deg, transparent, #2196f3, transparent)',
            animation: 'processing-pulse 2s ease-in-out infinite'
          }}
        />
      )}
      
      <style>
        {`
          @keyframes processing-pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
});

MeetingTabs.displayName = 'MeetingTabs';

export default MeetingTabs; 